import React, { useState, useEffect, Fragment } from 'react';
import { Input, Table, message, Modal, Tag, Button, Space, Tooltip, Form, Select, DatePicker } from "antd";
import { ReloadOutlined, EyeOutlined, CheckOutlined, CloseOutlined, UserOutlined } from '@ant-design/icons';
import useRequest from '@/utils/useRequest';
import useWscTableList from '@/component/UseWscTableList';
import useWscCustomColumns from '@/component/UseWscCustomColumns';
import ColumnSettings from '@/component/ColumnSettings';
import { useCommonStore } from '@/store/useCommonStore';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(duration);
dayjs.extend(relativeTime);

const { Search } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;

const initParams = {
  "filters": {
    // 移除固定的筛选条件，让用户可以看到所有任务
  },
  "pagination": {
    "current": 1,
    "pageSize": 10
  },
  "sorter": {
    "field": "created_at",
    "order": "desc"
  }
};

const BpmnTasks = () => {
  const { request } = useRequest();
  
  // 使用useWscTableList连接后端服务
  const {
    list,
    setPage,
    setPageSize,
    updatePagination,
    setFilter,
    filters,
    setSorter,
    pagination,
    Refresh,
    isLoading
  } = useWscTableList({
    url: 'http://127.0.0.1:8080/api/v1/tasks/query',
    initParams
  });
  const { Refresh: refreshColumns, colsettings } = useWscCustomColumns({ key: "bpmn_tasks" });
  const [columns, setColumns] = useState([]);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [assigneeFilter, setAssigneeFilter] = useState('');
  const [includeCompleted, setIncludeCompleted] = useState(false);
  const [dateRange, setDateRange] = useState([]);
  
  // 获取当前用户信息
  const user = useCommonStore.use.user();

  // 默认列配置
  const defaultColumns = [
    {
      title: '任务ID',
      dataIndex: 'id',
      key: 'id',
      width: 120,
      ellipsis: true,
      visible: true
    },
    {
      title: '任务名称',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      visible: true,
      render: (text) => (
        <Tooltip title={text}>
          <span>{text || '-'}</span>
        </Tooltip>
      )
    },
    {
      title: '任务定义Key',
      dataIndex: 'task_definition_key',
      key: 'task_definition_key',
      width: 150,
      ellipsis: true,
      visible: true
    },
    {
      title: '流程实例ID',
      dataIndex: 'process_instance_id',
      key: 'process_instance_id',
      width: 150,
      ellipsis: true,
      visible: true,
      render: (text) => (
        <Tooltip title={text}>
          <span className="text-blue-600 cursor-pointer hover:underline">{text || '-'}</span>
        </Tooltip>
      )
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      align: 'center',
      visible: true,
      render: (status) => {
        const statusConfig = {
          'Created': { color: 'blue', text: '已创建' },
          'Assigned': { color: 'orange', text: '已分配' },
          'InProgress': { color: 'processing', text: '进行中' },
          'Completed': { color: 'success', text: '已完成' },
          'Cancelled': { color: 'default', text: '已取消' },
          'Suspended': { color: 'warning', text: '挂起' }
        };
        const config = statusConfig[status] || { color: 'default', text: status };
        return <Tag color={config.color}>{config.text}</Tag>;
      }
    },
    {
      title: '分配人',
      dataIndex: 'assignee',
      key: 'assignee',
      width: 120,
      visible: true,
      render: (assignee) => (
        <div className="flex items-center">
          <UserOutlined className="mr-1 text-gray-400" />
          <span>{assignee || '未分配'}</span>
        </div>
      )
    },
    {
      title: '候选组',
      dataIndex: 'candidate_groups',
      key: 'candidate_groups',
      width: 150,
      visible: true,
      render: (groups) => {
        if (!groups || groups.length === 0) return '-';
        return (
          <div className="flex flex-wrap gap-1">
            {groups.slice(0, 2).map((group, index) => (
              <Tag key={index} size="small">{group}</Tag>
            ))}
            {groups.length > 2 && (
              <Tooltip title={groups.slice(2).join(', ')}>
                <Tag size="small">+{groups.length - 2}</Tag>
              </Tooltip>
            )}
          </div>
        );
      }
    },
    {
      title: '优先级',
      dataIndex: 'priority',
      key: 'priority',
      width: 80,
      align: 'center',
      visible: true,
      render: (priority) => {
        const priorityConfig = {
          1: { color: 'red', text: '高' },
          2: { color: 'orange', text: '中' },
          3: { color: 'green', text: '低' }
        };
        const config = priorityConfig[priority] || { color: 'default', text: priority || '-' };
        return <Tag color={config.color}>{config.text}</Tag>;
      }
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      visible: true,
      sorter: true,
      render: (time) => time ? dayjs(time).format('YYYY-MM-DD HH:mm:ss') : '-'
    },
    {
      title: '到期时间',
      dataIndex: 'due_date',
      key: 'due_date',
      width: 160,
      visible: true,
      render: (time) => {
        if (!time) return '-';
        const dueDate = dayjs(time);
        const now = dayjs();
        const isOverdue = dueDate.isBefore(now);
        return (
          <span className={isOverdue ? 'text-red-500 font-medium' : ''}>
            {dueDate.format('YYYY-MM-DD HH:mm:ss')}
            {isOverdue && <span className="ml-1">(已逾期)</span>}
          </span>
        );
      }
    },
    {
      title: '完成时间',
      dataIndex: 'completed_time',
      key: 'completed_time',
      width: 160,
      visible: true,
      render: (time) => time ? dayjs(time).format('YYYY-MM-DD HH:mm:ss') : '-'
    }
  ];

  const ExtendColumns = [
    {
      title: <div>操作 <ColumnSettings topic="bpmn_tasks" colsettings={colsettings} list={list} onFinish={refreshColumns} /></div>,
      dataIndex: 'id',
      key: 'id',
      width: 200,
      fixed: 'right',
      render: (id, record) => (
        <Fragment>
          <Button 
            type="link" 
            icon={<EyeOutlined />} 
            onClick={() => handleViewDetail(record)}
          >
            详情
          </Button>
          {record.status !== 'Completed' && record.status !== 'Cancelled' && (
            <>
              <Button 
                type="link" 
                icon={<CheckOutlined />} 
                onClick={() => handleComplete(record)}
              >
                完成
              </Button>
              <Button 
                type="link" 
                danger
                icon={<CloseOutlined />} 
                onClick={() => handleCancel(record)}
              >
                取消
              </Button>
            </>
          )}
        </Fragment>
      )
    }
  ];

  // 初始化加载
  useEffect(() => {
    // 使用自定义列设置或默认列设置
    const baseColumns = colsettings && colsettings.length > 0 
      ? colsettings.filter(item => item.visible) 
      : defaultColumns.filter(item => item.visible);
    
    let cols = baseColumns.concat(ExtendColumns);
    setColumns(cols);
  }, [colsettings]);

  // 处理表格变化（分页、排序、筛选）
  const handleTableChange = (pagination, _filters, sorter) => {
    setPage(pagination.current);
    
    // 处理排序
    if (sorter && sorter.field) {
      setSorter({
        field: sorter.field,
        order: sorter.order === 'ascend' ? 'asc' : 'desc'
      });
    } else {
      setSorter(null);
    }
    
    setFilter({ ...filters, ..._filters });
  };

  // 处理搜索
  const handleSearch = (value) => {
    setSearchKeyword(value);
    
    const newFilters = { ...filters };
    if (value && value.trim()) {
      newFilters.keywords = value.trim();
    } else {
      delete newFilters.keywords;
    }
    
    // 应用其他筛选条件
    if (statusFilter) {
      newFilters.status = statusFilter;
    }
    if (assigneeFilter && assigneeFilter.trim()) {
      newFilters.assignee = assigneeFilter.trim();
    }
    if (dateRange && dateRange.length === 2) {
      newFilters.created_time_start = dateRange[0].format('YYYY-MM-DD HH:mm:ss');
      newFilters.created_time_end = dateRange[1].format('YYYY-MM-DD HH:mm:ss');
    }
    newFilters.include_completed = includeCompleted;
    
    setFilter(newFilters);
    updatePagination({ current: 1 });
  };

  // 处理状态筛选
  const handleStatusFilter = (value) => {
    setStatusFilter(value);
    
    const newFilters = { ...filters };
    if (value) {
      newFilters.status = value;
    } else {
      delete newFilters.status;
    }
    
    // 应用其他筛选条件
    if (searchKeyword && searchKeyword.trim()) {
      newFilters.keywords = searchKeyword.trim();
    }
    if (assigneeFilter && assigneeFilter.trim()) {
      newFilters.assignee = assigneeFilter.trim();
    }
    if (dateRange && dateRange.length === 2) {
      newFilters.created_time_start = dateRange[0].format('YYYY-MM-DD HH:mm:ss');
      newFilters.created_time_end = dateRange[1].format('YYYY-MM-DD HH:mm:ss');
    }
    newFilters.include_completed = includeCompleted;
    
    setFilter(newFilters);
    updatePagination({ current: 1 });
  };

  // 处理分配人筛选
  const handleAssigneeFilter = (value) => {
    setAssigneeFilter(value);
    
    const newFilters = { ...filters };
    if (value && value.trim()) {
      newFilters.assignee = value.trim();
    } else {
      delete newFilters.assignee;
    }
    
    // 应用其他筛选条件
    if (searchKeyword && searchKeyword.trim()) {
      newFilters.keywords = searchKeyword.trim();
    }
    if (statusFilter) {
      newFilters.status = statusFilter;
    }
    if (dateRange && dateRange.length === 2) {
      newFilters.created_time_start = dateRange[0].format('YYYY-MM-DD HH:mm:ss');
      newFilters.created_time_end = dateRange[1].format('YYYY-MM-DD HH:mm:ss');
    }
    newFilters.include_completed = includeCompleted;
    
    setFilter(newFilters);
    updatePagination({ current: 1 });
  };

  // 处理包含已完成任务
  const handleIncludeCompleted = (e) => {
    const checked = e.target.checked;
    setIncludeCompleted(checked);
    
    const newFilters = { ...filters };
    newFilters.include_completed = checked;
    
    // 应用其他筛选条件
    if (searchKeyword && searchKeyword.trim()) {
      newFilters.keywords = searchKeyword.trim();
    }
    if (statusFilter) {
      newFilters.status = statusFilter;
    }
    if (assigneeFilter && assigneeFilter.trim()) {
      newFilters.assignee = assigneeFilter.trim();
    }
    if (dateRange && dateRange.length === 2) {
      newFilters.created_time_start = dateRange[0].format('YYYY-MM-DD HH:mm:ss');
      newFilters.created_time_end = dateRange[1].format('YYYY-MM-DD HH:mm:ss');
    }
    
    setFilter(newFilters);
    updatePagination({ current: 1 });
  };

  // 处理日期范围筛选
  const handleDateRangeFilter = (dates) => {
    setDateRange(dates);
    
    const newFilters = { ...filters };
    if (dates && dates.length === 2) {
      newFilters.created_time_start = dates[0].format('YYYY-MM-DD HH:mm:ss');
      newFilters.created_time_end = dates[1].format('YYYY-MM-DD HH:mm:ss');
    } else {
      delete newFilters.created_time_start;
      delete newFilters.created_time_end;
    }
    
    // 应用其他筛选条件
    if (searchKeyword && searchKeyword.trim()) {
      newFilters.keywords = searchKeyword.trim();
    }
    if (statusFilter) {
      newFilters.status = statusFilter;
    }
    if (assigneeFilter && assigneeFilter.trim()) {
      newFilters.assignee = assigneeFilter.trim();
    }
    newFilters.include_completed = includeCompleted;
    
    setFilter(newFilters);
    updatePagination({ current: 1 });
  };

  // 重置搜索
  const handleReset = () => {
    setSearchKeyword('');
    setStatusFilter('');
    setAssigneeFilter('');
    setIncludeCompleted(false);
    setDateRange([]);
    setFilter(initParams.filters);
    setSorter(initParams.sorter);
    setPage(1);
  };

  // 刷新列表
  const handleRefresh = () => {
    Refresh();
  };

  // 任务认领
  const handleClaimTask = async (taskId) => {
    try {
      // 检查用户是否已登录
      if (!user || !user.Name) {
        message.error('请先登录');
        return;
      }
      
      const response = await request(`http://127.0.0.1:8080/api/v1/tasks/${taskId}/claim`, 'POST', 'json', {
        assignee: user.Name
      });
      if (response.success) {
        message.success('任务认领成功');
        Refresh();
      } else {
        message.error(response.message || '任务认领失败');
      }
    } catch (error) {
      console.error('任务认领失败:', error);
      message.error('任务认领失败');
    }
  };

  // 取消认领
  const handleUnclaimTask = async (taskId) => {
    try {
      const response = await request(`http://127.0.0.1:8080/api/v1/tasks/${taskId}/unclaim`, 'POST', 'json', {});
      if (response.success) {
        message.success('取消认领成功');
        Refresh();
      } else {
        message.error(response.message || '取消认领失败');
      }
    } catch (error) {
      console.error('取消认领失败:', error);
      message.error('取消认领失败');
    }
  };

  // 任务分配
  const handleAssignTask = async (taskId, assignee) => {
    try {
      const response = await request(`http://127.0.0.1:8080/api/v1/tasks/${taskId}/assign`, 'POST', 'json', {
        assignee: assignee
      });
      if (response.success) {
        message.success('任务分配成功');
        Refresh();
      } else {
        message.error(response.message || '任务分配失败');
      }
    } catch (error) {
      console.error('任务分配失败:', error);
      message.error('任务分配失败');
    }
  };

  // 用户反馈提交
  const handleSubmitFeedback = async (taskId, feedback, variables) => {
    try {
      const response = await request(`http://127.0.0.1:8080/api/v1/tasks/${taskId}/feedback`, 'POST', 'json', {
        feedback: feedback,
        variables: variables
      });
      if (response.success) {
        message.success('反馈提交成功');
        Refresh();
      } else {
        message.error(response.message || '反馈提交失败');
      }
    } catch (error) {
      console.error('反馈提交失败:', error);
      message.error('反馈提交失败');
    }
  };

  // 查看详情
  const handleViewDetail = async (record) => {
    try {
      // 调用获取任务详情API
      const response = await request(`http://127.0.0.1:8080/api/v1/tasks/${record.id}`, 'POST', 'json', {});
      
      if (!response.success) {
        message.error(response.message || '获取任务详情失败');
        return;
      }
      
      const taskDetail = response.data;
      
      Modal.info({
        title: (
          <div className="flex items-center">
            <EyeOutlined className="mr-2 text-blue-500" />
            <span>任务详情</span>
          </div>
        ),
        width: 800,
        content: (
          <div className="space-y-6">
            {/* 基本信息卡片 */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-lg border border-blue-200">
              <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
                基本信息
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white p-3 rounded border">
                  <div className="flex items-center mb-1">
                    <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                    <span className="text-sm text-gray-600">任务ID</span>
                  </div>
                  <div className="font-medium text-gray-800">{taskDetail.id}</div>
                </div>
                <div className="bg-white p-3 rounded border">
                  <div className="flex items-center mb-1">
                    <div className="w-2 h-2 bg-purple-500 rounded-full mr-2"></div>
                    <span className="text-sm text-gray-600">任务名称</span>
                  </div>
                  <div className="font-medium text-gray-800">{taskDetail.name || '-'}</div>
                </div>
                <div className="bg-white p-3 rounded border">
                  <div className="flex items-center mb-1">
                    <div className="w-2 h-2 bg-orange-500 rounded-full mr-2"></div>
                    <span className="text-sm text-gray-600">状态</span>
                  </div>
                  <div className="font-medium">
                    <Tag color={taskDetail.status === 'Completed' ? 'green' : taskDetail.status === 'InProgress' ? 'processing' : 'blue'}>
                      {taskDetail.status === 'Completed' ? '已完成' : taskDetail.status === 'InProgress' ? '进行中' : '待处理'}
                    </Tag>
                  </div>
                </div>
                <div className="bg-white p-3 rounded border">
                  <div className="flex items-center mb-1">
                    <div className="w-2 h-2 bg-red-500 rounded-full mr-2"></div>
                    <span className="text-sm text-gray-600">分配人</span>
                  </div>
                  <div className="font-medium text-gray-800">{taskDetail.assignee || '未分配'}</div>
                </div>
                <div className="bg-white p-3 rounded border">
                  <div className="flex items-center mb-1">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
                    <span className="text-sm text-gray-600">创建时间</span>
                  </div>
                  <div className="font-medium text-gray-800">
                    {taskDetail.created_time ? dayjs(taskDetail.created_time).format('YYYY-MM-DD HH:mm:ss') : '-'}
                  </div>
                </div>
                <div className="bg-white p-3 rounded border">
                  <div className="flex items-center mb-1">
                    <div className="w-2 h-2 bg-gray-500 rounded-full mr-2"></div>
                    <span className="text-sm text-gray-600">到期时间</span>
                  </div>
                  <div className="font-medium text-gray-800">
                    {taskDetail.due_date ? dayjs(taskDetail.due_date).format('YYYY-MM-DD HH:mm:ss') : '-'}
                  </div>
                </div>
              </div>
            </div>

            {/* 任务描述 */}
            {taskDetail.description && (
              <div className="bg-gradient-to-r from-gray-50 to-slate-50 p-4 rounded-lg border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                  <div className="w-3 h-3 bg-gray-500 rounded-full mr-2"></div>
                  任务描述
                </h3>
                <div className="bg-white p-3 rounded border">
                  <div className="text-gray-700 whitespace-pre-wrap">{taskDetail.description}</div>
                </div>
              </div>
            )}

            {/* 表单变量 */}
            {taskDetail.form_variables && Object.keys(taskDetail.form_variables).length > 0 && (
              <div className="bg-gradient-to-r from-yellow-50 to-amber-50 p-4 rounded-lg border border-yellow-200">
                <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                  <div className="w-3 h-3 bg-yellow-500 rounded-full mr-2"></div>
                  表单变量
                </h3>
                <div className="grid grid-cols-1 gap-3">
                  {Object.entries(taskDetail.form_variables).map(([key, value]) => (
                    <div key={key} className="bg-white p-3 rounded border">
                      <div className="flex items-center mb-1">
                        <div className="w-2 h-2 bg-yellow-500 rounded-full mr-2"></div>
                        <span className="text-sm text-gray-600 font-medium">{key}</span>
                      </div>
                      <div className="text-gray-800">{JSON.stringify(value)}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 操作按钮区域 */}
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-lg border border-green-200">
              <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                任务操作
              </h3>
              <div className="flex flex-wrap gap-2">
                {/* 根据任务状态显示不同的操作按钮 */}
                {!taskDetail.assignee && (
                  <Button 
                    type="primary" 
                    icon={<UserOutlined />}
                    onClick={() => {
                      Modal.destroyAll();
                      handleClaimTask(taskDetail.id);
                    }}
                  >
                    认领任务
                  </Button>
                )}
                
                {taskDetail.assignee && taskDetail.status !== 'Completed' && (
                  <Button 
                    type="default" 
                    icon={<CloseOutlined />}
                    onClick={() => {
                      Modal.destroyAll();
                      handleUnclaimTask(taskDetail.id);
                    }}
                  >
                    取消认领
                  </Button>
                )}
                
                <Button 
                  type="default" 
                  icon={<UserOutlined />}
                  disabled={taskDetail.status === 'Completed'}
                  onClick={() => {
                    if (taskDetail.status === 'Completed') {
                      message.warning('已完成的任务无法重新分配');
                      return;
                    }
                    Modal.destroyAll();
                    // 弹出分配对话框
                    Modal.confirm({
                      title: '分配任务',
                      content: (
                        <div>
                          <p>请输入要分配的用户名：</p>
                          <Input 
                            id="assignee-input"
                            placeholder="请输入用户名" 
                            style={{ marginTop: 8 }}
                          />
                        </div>
                      ),
                      onOk: () => {
                        const assignee = document.getElementById('assignee-input').value;
                        if (assignee.trim()) {
                          handleAssignTask(taskDetail.id, assignee.trim());
                        } else {
                          message.error('请输入用户名');
                          return false;
                        }
                      }
                    });
                  }}
                >
                  {taskDetail.status === 'Completed' ? '任务已完成' : '分配任务'}
                </Button>
                
                {taskDetail.status !== 'Completed' && (
                  <Button 
                    type="primary" 
                    icon={<CheckOutlined />}
                    onClick={() => {
                      Modal.destroyAll();
                      // 弹出反馈提交对话框
                      Modal.confirm({
                        title: '提交反馈',
                        width: 600,
                        content: (
                          <div>
                            <div style={{ marginBottom: 16 }}>
                              <label>反馈内容：</label>
                              <Input.TextArea 
                                id="feedback-input"
                                placeholder="请输入反馈内容" 
                                rows={4}
                                style={{ marginTop: 8 }}
                              />
                            </div>
                            <div>
                              <label>自定义变量（JSON格式）：</label>
                              <Input.TextArea 
                                id="variables-input"
                                placeholder='例如：{"result": "success", "score": 95}' 
                                rows={3}
                                style={{ marginTop: 8 }}
                              />
                            </div>
                          </div>
                        ),
                        onOk: () => {
                          const feedback = document.getElementById('feedback-input').value;
                          const variablesStr = document.getElementById('variables-input').value;
                          let variables = {};
                          
                          if (variablesStr.trim()) {
                            try {
                              variables = JSON.parse(variablesStr);
                            } catch (e) {
                              message.error('自定义变量格式错误，请输入有效的JSON格式');
                              return false;
                            }
                          }
                          
                          handleSubmitFeedback(taskDetail.id, feedback, variables);
                        }
                      });
                    }}
                  >
                    提交反馈
                  </Button>
                )}
              </div>
            </div>
          </div>
        ),
        okText: '关闭',
        onOk: () => {}
      });
      
      message.success('获取任务详情成功');
    } catch (error) {
      console.error('获取任务详情失败:', error);
      message.error('获取任务详情失败');
    }
  };

  // 完成任务
  const handleComplete = (record) => {
    // 默认参数
    const defaultParams = {
      variables: {
        result: "completed",
        comment: "任务已完成",
        completed_date: new Date().toISOString()
      },
      completed_by: user?.Name || user?.id || "unknown"
    };

    let customParams = JSON.stringify(defaultParams, null, 2);

    Modal.confirm({
      title: '完成任务',
      width: 600,
      content: (
        <div>
          <p className="mb-4">任务: {record.name || record.id}</p>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              完成参数 (JSON格式):
            </label>
            <Input.TextArea
              rows={8}
              defaultValue={customParams}
              onChange={(e) => {
                customParams = e.target.value;
              }}
              placeholder="请输入JSON格式的完成参数"
              style={{ fontFamily: 'monospace' }}
            />
            <div className="mt-2 text-xs text-gray-500">
              提示: 可以修改variables中的参数，如result、comment等
            </div>
          </div>
        </div>
      ),
      okText: '确认完成',
      cancelText: '取消',
      onOk: async () => {
        try {
          // 验证JSON格式
          let params;
          try {
            params = JSON.parse(customParams);
          } catch (e) {
            message.error('参数格式错误，请输入有效的JSON格式');
            return false;
          }

          // 调用完成任务API
          const response = await request(`http://127.0.0.1:8080/api/v1/tasks/${record.id}/complete`, 'POST', 'json', params);
          
          if (response.success) {
            message.success('任务完成成功');
            // 刷新列表数据
            Refresh();
          } else {
            message.error(response.message || '完成任务失败');
          }
        } catch (error) {
          console.error('完成任务失败:', error);
          message.error('完成任务失败');
        }
      }
    });
  };

  // 取消任务
  const handleCancel = (record) => {
    Modal.confirm({
      title: '确认取消',
      content: `您确定要取消任务 "${record.name || record.id}" 吗？`,
      okText: '确认',
      cancelText: '取消',
      okType: 'danger',
      onOk: async () => {
        try {
          // 调用取消任务API
          const response = await request(`http://127.0.0.1:8080/api/bpmn/tasks/${record.id}/cancel`, 'POST', 'json', {
            taskId: record.id,
            reason: '用户主动取消'
          });
          
          if (response.success) {
            message.success('任务已取消');
            // 刷新列表数据
            Refresh();
          } else {
            message.error(response.message || '取消任务失败');
          }
        } catch (error) {
          console.error('取消任务失败:', error);
          message.error('取消任务失败');
        }
      }
    });
  };

  return (
    <div style={{ maxHeight: 'calc(100vh - 120px)', overflowY: 'scroll' }} className="bg-white pl-4 pb-4">
      {/* 筛选区域 */}
      <div className="mb-4 p-4 bg-gray-50 rounded-lg">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">关键词搜索</label>
            <Search 
              placeholder="请输入任务名称或ID" 
              onSearch={(value) => {
                console.log('点击搜索按钮');
                handleSearch(value);
              }}
              value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
              enterButton 
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">状态筛选</label>
            <Select
              placeholder="选择状态"
              value={statusFilter}
              onChange={handleStatusFilter}
              allowClear
              style={{ width: '100%' }}
            >
              <Option value="Created">已创建</Option>
              <Option value="Assigned">已分配</Option>
              <Option value="InProgress">进行中</Option>
              <Option value="Completed">已完成</Option>
              <Option value="Cancelled">已取消</Option>
              <Option value="Suspended">挂起</Option>
            </Select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">分配人筛选</label>
            <Input
              placeholder="请输入分配人"
              value={assigneeFilter}
              onChange={(e) => handleAssigneeFilter(e.target.value)}
              allowClear
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">创建时间</label>
            <RangePicker
              value={dateRange}
              onChange={handleDateRangeFilter}
              showTime
              format="YYYY-MM-DD HH:mm:ss"
              style={{ width: '100%' }}
            />
          </div>
        </div>
        <div className="mt-4 flex items-center justify-between">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="includeCompleted"
              checked={includeCompleted}
              onChange={handleIncludeCompleted}
              className="mr-2"
            />
            <label htmlFor="includeCompleted" className="text-sm text-gray-700">
              包含已完成任务
            </label>
          </div>
          <div className="flex space-x-2">
            <Button onClick={handleReset}>
              重置
            </Button>
            <Button 
              type="primary" 
              icon={<ReloadOutlined />} 
              onClick={handleRefresh}
              loading={isLoading}
            >
              刷新
            </Button>
          </div>
        </div>
      </div>

      <Table
        rowKey="id"
        loading={isLoading}
        bordered
        dataSource={list}
        columns={columns}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条/共 ${total} 条`
        }}
        onChange={handleTableChange}
        size="small"
        scroll={{ x: 1600 }}
      />
    </div>
  );
};

export default BpmnTasks;